import { useCallback, useMemo } from 'react';
import type { ValueSelectorProps } from '../types';
import { toOptions } from '../utils';
import { ReactSelectWrapped } from './ReactSelectWrapped';
import type { MultiValue, SingleValue, PropsValue } from 'react-select';

export const ValueSelector = (props: ValueSelectorProps) => {
  const onChange = useCallback(
    (
      selectValue:
        | MultiValue<string | { label: string; value: string }>
        | SingleValue<string | { label: string; value: string }>
    ) => {
      if (props.multiple) {
        props.handleOnChange(
          [...(selectValue as MultiValue<string | { label: string; value: string }>)]
            .map(o => (o as { label: string; value: string }).value)
            .join(',')
        );
      } else {
        props.handleOnChange((selectValue as { label: string; value: string }).value);
      }
    },
    [props.handleOnChange, props.multiple]
  );

  const options = toOptions(props.options) as { label: string; value: string }[];
  const defaultValue: PropsValue<string | { label: string; value: string }> | undefined =
    useMemo(() => {
      if (props.value) {
        if (props.multiple) {
          return props.value.split(',');
        } else {
          return (
            [...options].find(option => option.value === props.value) ??
            ({
              value: props.value,
              label: props.value,
            } as SingleValue<string | { label: string; value: string }>)
          );
        }
      }
      return undefined;
    }, [props.value, props.multiple]);

  if (props.debugMode && props.onLog) {
    props.onLog('ValueSelector', { testID: props.testID, options, defaultValue, props });
  }
  return (
    <ReactSelectWrapped
      data-testid={props.testID}
      className={props.className}
      value={defaultValue}
      name={props.title}
      isDisabled={props.disabled}
      isMulti={!!props.multiple}
      isCreatable={props.testID === 'fields'}
      onChange={onChange}
      options={options}
    />
  );
};

ValueSelector.displayName = 'ValueSelector';
